import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { InlineInputField, InlineSelect, InputField, Label } from "components/custom/programs/fields";
import { Col, Form, Nav, Row } from "react-bootstrap";
import { BudgetCapFields, CheckboxField, FlightFields, SelectField } from "./fields";
import {
  API_FIELD_MAPPINGS,
  FREQUENCY_TIME_UNIT_OPTIONS,
  ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS,
  ORDER_BID_OPTIMIZATION_STATEGY_OPTIONS,
  ORDER_FORM_DEFAULTS,
  ORDER_GOAL_KPI_OPTIONS_BY_GOAL,
  ORDER_GOAL_OPTIONS,
  ORDER_STATE_OPTIONS,
} from "./constants";
import {
  castNumbersInFormData,
  formatOrderFormData,
  resolveOrderFormFieldDependencies,
  validateMandatoryOrderFormFields,
} from "./utils";
import { useMemo, useRef } from "react";
import { Button, buttonRecipe } from "components/core/basic/Button";
import { useMutation } from "@tanstack/react-query";
import ConfirmationModal from "components/core/blocks/ConfirmationModal";
import { successToast, errorToast } from "utils/toasts";
import moment from "moment";
import useOnScreen from "hooks/useOnScreen";
import axios from "axios";
import Tags from "components/core/blocks/Tags";
import { ProductsTable } from "components/core/blocks/ProductsTable";
import useProducts from "hooks/useProducts";
import { useAppSelector } from "redux/hooks";
import Switch from "react-bootstrap-switch";
import Select from "react-select";
import { css } from "../../../../styled-system/css";

const showAutomaticLineItemCreationOptions = false;
const EMPTY_ARRAY = [];
const EXCLUDED_ORDER_API_FIELDS = ["conversionTrackingAsins"];

const DSPOrderForm = ({
  initialFormData = {},
  createMode = false,
  onCancel = () => {},
  onSuccessfulSubmit = () => {},
  dspAdvertiserId,
  campaignId = null,
  orderEndDate = null,
}) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const user = useAppSelector((state) => state.user);

  const fieldColumnWidth = createMode ? 8 : 4;
  const errorClassNames = createMode ? "d-block ms-3 mt-2 fs-5" : "d-block ms-2 mt-0";
  const entityId = initialFormData?.id;

  const sections = [
    { key: "overview", label: "Overview" },
    { key: "goals", label: "Goal" },
    { key: "delivery", label: "Delivery" },
    { key: "conversionTracking", label: "Conversion Tracking" },
    { key: "targeting", label: "Targeting & Optimization" },
  ];

  const overviewSectionRef = useRef();
  const goalSectionRef = useRef();
  const deliverySectionRef = useRef();
  const conversionTrackingSectionRef = useRef();
  const targetingSectionRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibility = [];
  visibility[0] = useOnScreen(overviewSectionRef);
  visibility[1] = useOnScreen(goalSectionRef);
  visibility[2] = useOnScreen(deliverySectionRef);
  visibility[3] = useOnScreen(conversionTrackingSectionRef);
  visibility[4] = useOnScreen(targetingSectionRef);

  const sectionRefMapping = {
    overview: overviewSectionRef,
    goals: goalSectionRef,
    delivery: deliverySectionRef,
    targeting: targetingSectionRef,
  };

  const greatestVisibleStep = useMemo(() => {
    return visibility.lastIndexOf(true);
  }, [visibility]);

  const {
    control,
    register,
    setValue,
    reset,
    formState: { errors, dirtyFields },
    setError,
    clearErrors,
  } = useForm({
    mode: "onBlur",
    defaultValues:
      createMode ? { ...ORDER_FORM_DEFAULTS, advertiserId: dspAdvertiserId } : formatOrderFormData(initialFormData),
  });

  const formState = useWatch({ control });

  const productReportFilters = {
    mp: marketPlace,
    channel: channel.currentChannel,
    date_group: "day",
    empty: true,
    group_by: "product_id",
    start_date: moment().subtract(31, "days").format("YYYY-MM-DD"),
    end_date: moment().subtract(1, "days").format("YYYY-MM-DD"),
    asins: formState.conversionTrackingAsins?.map((product) => product.productId)?.join(","),
  };

  const { productData, isLoading: isProductDataLoading } = useProducts(
    true,
    null,
    null,
    productReportFilters,
    !!formState.conversionTrackingAsins?.length,
  );

  const {
    fields: budgetCapFields,
    append: appendBudgetCap,
    remove: removeBudgetCap,
  } = useFieldArray({
    control,
    name: "budgetCaps",
  });

  const {
    fields: flightFields,
    append: appendFlight,
    remove: removeFlight,
  } = useFieldArray({
    control,
    name: "flights",
  });

  // Below handles cases where form state needs to change in dependent fields
  resolveOrderFormFieldDependencies(formState, budgetCapFields, setValue);

  const updateOrder = useMutation({
    mutationFn: async () => {
      let updateData = {};
      Object.keys(dirtyFields)
        ?.filter((key) => !EXCLUDED_ORDER_API_FIELDS.includes(key))
        .forEach((dirtyFieldKey) => {
          updateData[API_FIELD_MAPPINGS[dirtyFieldKey]] = formState[dirtyFieldKey];
        });

      updateData = castNumbersInFormData(updateData);

      if ("optimization" in updateData && "goalSetting" in updateData.optimization) {
        updateData.optimization.goalSetting = {
          kpi: updateData.optimization.goalSetting.kpi,
          targetKpi: updateData.optimization.goalSetting.targetKpi,
        };
      }

      if ("flights" in updateData) {
        updateData["flights"] = {
          flights: updateData["flights"],
        };
      }

      let res;

      const processOrderUpdates = Object.keys(updateData)?.filter((key) => key !== "conversionTrackingAsins").length;

      if (processOrderUpdates) {
        await axios.patch(`/dsp/order/${entityId}/`, updateData);
      }

      if (dirtyFields.conversionTrackingAsins) {
        const trackedProductsData = {
          asins: formState.conversionTrackingAsins,
          campaignId: campaignId,
          marketplace: marketPlace,
        };

        const conversionTrackingProductsRes = await axios.post(
          "/dsp/update_conversion_tracking_products/",
          trackedProductsData,
        );

        // Handle cases where goal type requires tracking products before order can be activated
        if (
          ["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
          formState.state === "ACTIVE"
        ) {
          const updateData = {
            state: formState.state,
          };
          const stateUpdateRes = await axios.patch(`/dsp/order/${entityId}/`, updateData);
          return stateUpdateRes;
        }

        return conversionTrackingProductsRes;
      }

      return res;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      successToast({
        message: "Order successfully updated",
      });
      onSuccessfulSubmit();
    },

    onError: (error) => {
      errorToast({
        message: "Failed to update order",
        error,
      });
    },
  });

  const createOrder = useMutation({
    mutationFn: async () => {
      const fields = [
        "budgetCaps",
        "comments",
        "optimization",
        "name",
        "frequencies",
        "flights",
        "advertiserId",
        "fees",
      ];

      let createData = {};

      fields.forEach((key) => {
        createData[API_FIELD_MAPPINGS[key]] = formState[key];
      });

      createData = castNumbersInFormData(createData);

      if ("goalSetting" in createData.optimization) {
        createData.optimization.goalSetting = {
          kpi: createData.optimization.goalSetting.kpi,
          targetKpi: createData.optimization.goalSetting.targetKpi,
        };
      }

      createData["flights"] = { flights: createData["flights"] };

      const res = await axios.post(`/dsp/order/`, createData);

      if (formState.conversionTrackingAsins?.length) {
        const newCampaignId = res.data.campaign_id;

        const trackedProductsData = {
          asins: formState.conversionTrackingAsins,
          campaignId: newCampaignId,
          marketplace: marketPlace,
        };

        const conversionTrackingProductsRes = await axios.post(
          "/dsp/create_conversion_tracking_products/",
          trackedProductsData,
        );

        return conversionTrackingProductsRes;
      }

      return;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      successToast({
        message: "Order successfully created",
      });
      onSuccessfulSubmit();
    },

    onError: (error) => {
      errorToast({
        message: "Failed to create order",
        error,
      });
    },
  });

  if (
    ["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
    !formState?.conversionTrackingAsins?.length &&
    formState.state === "ACTIVE" &&
    !errors.conversionTrackingAsins
  ) {
    setError("conversionTrackingAsins", {
      type: "custom",
      message: "At least one conversion tracking ASIN is required for campaigns with a goal KPI of ROAS of CPSU.",
    });
  }

  if (
    ((["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
      formState?.conversionTrackingAsins?.length) ||
      !["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) ||
      formState.state !== "ACTIVE") &&
    errors.conversionTrackingAsins
  ) {
    clearErrors("conversionTrackingAsins");
  }

  return (
    <Row>
      <Col xs={12} md={createMode ? 3 : 2} style={{ borderRight: "1px solid #ccc" }}>
        <Nav
          variant="underline"
          onSelect={(tabId) => {
            sectionRefMapping[tabId].current.scrollIntoView({ behavior: "smooth" });
          }}
          activeKey={sections[greatestVisibleStep]?.key ?? "overview"}
          className="flex-column text-xs-center text-md-start"
        >
          {sections.map(({ key, label }) => (
            <Nav.Link eventKey={key} key={key} className="text-xs-center fs-4 text-md-start py-2">
              {label}
            </Nav.Link>
          ))}
        </Nav>
      </Col>
      <Col
        xs={12}
        md={createMode ? 9 : 10}
        className={createMode ? "p-5 custom-scroll-container" : "px-5"}
        style={createMode ? { overflowY: "auto", maxHeight: "90vh" } : {}}
      >
        {createMode && (
          <Row>
            <h2 className="fw-bold fs-1 ps-2">Create a New DSP Order</h2>
          </Row>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {/* Config */}
          <Row className="py-3 dsp-form-section" ref={overviewSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">Overview</Form.Label>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <InputField
                  label="Name"
                  placeholder="Name"
                  fieldName="name"
                  disabled={formState.deliveryStatus === "ENDED"}
                  errors={errors}
                  control={control}
                  rules={{
                    required: { value: true, message: "Name is required" },
                    minLength: { value: 2, message: "Name must be at least 1 character in length" },
                    maxLength: { value: 255, message: "Name must be 255 characters or less in length" },
                  }}
                  errorClassNames={errorClassNames}
                />
              </Col>
            </Row>
            {!createMode ?
              <Row className="py-3">
                <Col xs={12} lg={8} xl={fieldColumnWidth}>
                  <SelectField label={"State"} options={ORDER_STATE_OPTIONS} fieldName={"state"} control={control} />
                </Col>
              </Row>
            : null}

            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <InputField
                  label={
                    <>
                      Comments <small style={{ fontWeight: 400 }}>(optional)</small>
                    </>
                  }
                  fieldName="comments"
                  errors={errors}
                  control={control}
                  rules={{
                    maxLength: { value: 255, message: "Comments must be 255 characters or less in length" },
                  }}
                  errorClassNames={errorClassNames}
                />
              </Col>
            </Row>
          </Row>

          {/* Goal and Optimization */}
          <Row className="py-3 dsp-form-section" ref={goalSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">Goal and Optimization</Form.Label>

            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={"Goal"}
                  options={ORDER_GOAL_OPTIONS}
                  fieldName={"optimization.goalSetting.goal"}
                  control={control}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={"KPI"}
                  options={
                    !formState?.optimization?.goalSetting?.goal ?
                      []
                    : ORDER_GOAL_KPI_OPTIONS_BY_GOAL[formState.optimization?.goalSetting?.goal]
                  }
                  fieldName={"optimization.goalSetting.kpi"}
                  control={control}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={fieldColumnWidth} xl={fieldColumnWidth / 2}>
                <InputField
                  label="KPI Target"
                  placeholder="Enter a KPI target"
                  fieldName="optimization.goalSetting.targetKpi"
                  errors={errors}
                  control={control}
                  rules={{}}
                  type="number"
                  disabled={formState.optimization?.goalSetting?.disableKpiTarget}
                  errorClassNames={errorClassNames}
                />
              </Col>
              <Col xs={12} lg={fieldColumnWidth} xl={fieldColumnWidth / 2} className="mt-auto pb-3">
                <CheckboxField
                  label={"Don't set a KPI target"}
                  fieldName="optimization.goalSetting.disableKpiTarget"
                  control={control}
                  onChange={(value) => {
                    if (value) {
                      setValue("optimization.goalSetting.targetKpi", 0);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={"Bid Optimization Strategy"}
                  options={ORDER_BID_OPTIMIZATION_STATEGY_OPTIONS}
                  fieldName={"optimization.bidStrategy"}
                  control={control}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <CheckboxField
                  label={"Automate Budget Allocation"}
                  fieldName={"optimization.automateBudgetAllocation"}
                  control={control}
                />
              </Col>
            </Row>
            {/* TODO: This field isn't visible in the console UI and is still in closed beta, but automatically creates line items */}
            {/* We probably don't want to show it in the update panel yet, but double-check if anyone knows more about it */}
            {createMode && showAutomaticLineItemCreationOptions && (
              <>
                <Col xs={12} lg={fieldColumnWidth} className="mt-auto pb-3">
                  <CheckboxField
                    label={"Automatically create ad groups based on KPI goal"}
                    fieldName="enableAutomatedAdGroupCreation"
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        setValue("optimization.automatedAdGroupCreationSettings", {
                          performancePlus: {
                            inventoryTypes: [],
                          },
                        });
                      } else {
                        setValue("optimization.automatedAdGroupCreationSettings", { performancePlus: {} });
                      }
                    }}
                  />
                </Col>
                {formState.enableAutomatedAdGroupCreation && (
                  <Col xs={12} lg={fieldColumnWidth} className="mt-auto pb-3 d-flex">
                    <CheckboxField
                      label={"Create video line item"}
                      fieldName="performancePlusVideoInventoryType"
                      control={control}
                      onChange={(value) => {
                        if (value) {
                          const updatedInventoryTypes =
                            (
                              Array.isArray(
                                formState.optimization?.automatedAdGroupCreationSettings.performancePlus
                                  ?.inventoryTypes,
                              )
                            ) ?
                              formState.optimization?.automatedAdGroupCreationSettings.performancePlus?.inventoryTypes
                            : [];
                          updatedInventoryTypes.push("VIDEO");

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        } else {
                          const updatedInventoryTypes =
                            formState.optimization?.automatedAdGroupCreationSettings.performancePlus.inventoryTypes?.filter(
                              (type) => type !== "VIDEO",
                            );

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        }
                      }}
                    />
                    <CheckboxField
                      label={"Create display line item"}
                      fieldName="performancePlusDisplayInventoryType"
                      control={control}
                      onChange={(value) => {
                        if (value) {
                          const updatedInventoryTypes =
                            (
                              Array.isArray(
                                formState.optimization?.automatedAdGroupCreationSettings.performancePlus
                                  ?.inventoryTypes,
                              )
                            ) ?
                              formState.optimization?.automatedAdGroupCreationSettings.performancePlus?.inventoryTypes
                            : [];
                          updatedInventoryTypes.push("DISPLAY");

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        } else {
                          const updatedInventoryTypes =
                            formState.optimization?.automatedAdGroupCreationSettings.performancePlus.inventoryTypes?.filter(
                              (type) => type !== "DISPLAY",
                            );

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        }
                      }}
                    />
                  </Col>
                )}
              </>
            )}
          </Row>

          <Row className="py-3 dsp-form-section" ref={deliverySectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">Delivery</Form.Label>
            <Row className="p-3">
              <Form.Label className="fw-bold fs-3 px-2">Flights</Form.Label>
              {flightFields.length ?
                flightFields.map((field, index) => {
                  return (
                    <FlightFields
                      key={field.id}
                      control={control}
                      errors={errors}
                      fieldName={`flights.${index}`}
                      register={register}
                      removeFlight={() => {
                        removeFlight(index);
                      }}
                      orderFlights={formState.flights}
                      index={index}
                      createMode={createMode}
                      orderEndDate={orderEndDate}
                      {...field}
                    />
                  );
                })
              : <span className={`text-danger ${errorClassNames} ps-0 mb-3`}>At least one flight is required</span>}
              <span
                className="fs-5 px-5 trellis-purple w-auto"
                onClick={() => {
                  const defaultStartDate = moment
                    .max(formState.flights.map((f) => moment(f.endDateTime)))
                    .add(1, "day");
                  const defaultEndDate = moment(defaultStartDate).add(1, "month");
                  appendFlight({
                    startDateTime: defaultStartDate.toISOString(),
                    endDateTime: defaultEndDate.toISOString(),
                    budgetAmount: 100,
                  });
                }}
                style={{
                  color: "#c43066",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Add new flight
              </span>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <Form.Group>
                  <Label>Budget Caps</Label>
                  <CheckboxField
                    label={"Set a daily or monthly spending limit"}
                    fieldName={"budgetCapsEnabled"}
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        appendBudgetCap({ amount: 0, recurrenceTimePeriod: "DAILY" });
                      } else {
                        removeBudgetCap();
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {budgetCapFields.length ?
              budgetCapFields.map((field, index) => {
                return (
                  <BudgetCapFields
                    key={field.id}
                    control={control}
                    fieldName={`budgetCaps.${index}`}
                    register={register}
                    errors={errors}
                    removeCap={() => {
                      removeBudgetCap(index);
                    }}
                    appendCap={() => {
                      appendBudgetCap({
                        amount: 0,
                        recurrenceTimePeriod:
                          formState.budgetCaps?.[0]?.recurrenceTimePeriod === "DAILY" ? "MONTHLY" : "DAILY",
                      });
                    }}
                    budgetCapCount={budgetCapFields.length}
                    index={index}
                    {...field}
                    createMode={createMode}
                  />
                );
              })
            : null}
          </Row>
          {user.is_staff ?
            <>
              <Row className="py-3">
                <Col xs={12} lg={8} xl={fieldColumnWidth}>
                  <Form.Group>
                    <Label>Agency Fees</Label>
                    <CheckboxField
                      label={"Apply an agency fee"}
                      fieldName={"agencyFeeEnabled"}
                      control={control}
                      onChange={(value) => {
                        if (!value) {
                          setValue("fees.agencyFeePercentage", 0, { shouldDirty: true });
                        }
                      }}
                      disabled={!createMode}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {formState.agencyFeeEnabled ?
                <Row>
                  <Col xs={12} lg={createMode ? 3 : 2}>
                    <InputField
                      label="Agency Fee Percent"
                      placeholder="%"
                      fieldName="fees.agencyFeePercentage"
                      disabled={!createMode}
                      errors={errors}
                      control={control}
                      rules={{
                        min: { value: 0, message: "Agency fee percent must be at least 0" },
                        max: { value: 99, message: "Agency fee percent cannot be greater than 99" },
                      }}
                      errorClassNames={errorClassNames}
                    />
                  </Col>
                </Row>
              : null}
            </>
          : null}
          {/* Conversion Tracking */}
          <Row className="py-3 dsp-form-section" ref={conversionTrackingSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2 pb-3">Conversion Tracking</Form.Label>
            {errors.conversionTrackingAsins ?
              <span className={`text-danger ${css({ marginBottom: "medium", textWrap: "nowrap", fontSize: "12px" })}`}>
                {errors.conversionTrackingAsins.message}
              </span>
            : null}
            <Col xs={12} lg={8} xl={fieldColumnWidth} className="pb-5">
              <SelectField
                label={"Amazon Domain"}
                options={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS}
                fieldName={"conversionTrackingDomain"}
                control={control}
              />
            </Col>
            <Label>Products (optional)</Label>
            <div style={{ overflow: "auto" }}>
              <Tags
                filledTags={formState.conversionTrackingAsins?.map((asin) => asin.productId)}
                saveFilledTags={(asins) => {
                  if (asins?.length > formState.conversionTrackingAsins?.length) {
                    const newAsins = asins
                      ?.map((asin) => asin.trim())
                      ?.filter(
                        (asin) =>
                          !formState.conversionTrackingAsins
                            ?.map((trackingSpec) => trackingSpec.productId)
                            ?.includes(asin),
                      )
                      ?.filter((newAsin) =>
                        newAsin
                          ?.toString()
                          .toLowerCase()
                          .match(/b0[a-z0-9]{8}/i),
                      );

                    const formattedNewAsins =
                      newAsins?.map((newAsin) => ({
                        productId: newAsin,
                        productAssociation: "FEATURED",
                        domain: formState.conversionTrackingDomain,
                      })) ?? [];
                    // Handle adding ASIN
                    const formattedAsins = [...formState.conversionTrackingAsins, ...formattedNewAsins];
                    setValue("conversionTrackingAsins", formattedAsins, { shouldDirty: true });
                  } else {
                    // Handle removing ASIN
                    const updatedAsins =
                      asins?.length ?
                        formState.conversionTrackingAsins?.filter((asin) => asins?.includes(asin.productId))
                      : [];
                    setValue("conversionTrackingAsins", updatedAsins, {
                      shouldDirty: true,
                    });
                  }
                }}
                placeholder={"Enter ASINs..."}
                className="pb-4"
              />
            </div>

            {formState.conversionTrackingAsins?.length ?
              <Col xs={12} className="pb-5">
                <ProductsTable
                  selectedProducts={[]}
                  marketPlace={marketPlace}
                  productData={
                    Object.values(
                      productData ?
                        productData.reduce((acc, product) => {
                          if (!acc[product.asin] || acc[product.asin].total_sales < product.total_sales) {
                            acc[product.asin] = product;
                          }
                          return acc;
                        }, {})
                      : [],
                    ) ?? EMPTY_ARRAY
                  }
                  isLoading={isProductDataLoading}
                  hideBulkActionsButton={false}
                  titleColWidth={250}
                  minRows={0}
                  isSelectable={false}
                  customColumns={[
                    {
                      key: "sales",
                      compareKey: "sales_percent",
                      options: { width: 85 },
                    },
                    {
                      key: "units",
                      compareKey: "units_percent",
                      options: { width: 85 },
                    },
                    {
                      key: "ad_sales",
                      compareKey: "ad_sales_percent",
                      options: { width: 85 },
                    },

                    {
                      key: "domain",
                      compareKey: null,
                      options: {
                        metric: {
                          id: "domain",
                          format: "text",
                          accessor: (d) => d,
                          name: "Domain",
                        },
                        formatter: (v) => {
                          const asinSpec = formState.conversionTrackingAsins?.find((asin) => asin.productId === v.asin);
                          return (
                            <div>
                              <Select
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9000 }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "12px",
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    width: "130px",
                                    marginLeft: "20px",
                                  }),
                                }}
                                menuPortalTarget={document.body}
                                options={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS}
                                defaultValue={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS.find(
                                  (option) => option.value === asinSpec?.domain,
                                )}
                                onChange={(value) => {
                                  const updatedDomain = value.value;
                                  const updatedAsins = formState.conversionTrackingAsins?.map((asin) => {
                                    if (asin.productId === v.asin) {
                                      return { ...asin, domain: updatedDomain };
                                    }
                                    return asin;
                                  });
                                  setValue("conversionTrackingAsins", updatedAsins, {
                                    shouldDirty: true,
                                  });
                                }}
                              />
                            </div>
                          );
                        },
                      },
                    },
                    {
                      key: "featured",
                      compareKey: null,
                      options: {
                        metric: {
                          id: "featured",
                          format: "text",
                          accessor: (d) => d,
                          name: "Featured",
                        },
                        formatter: (v) => {
                          const asinSpec = formState.conversionTrackingAsins?.find((asin) => asin.productId === v.asin);

                          return (
                            <Switch
                              onText=""
                              offText=""
                              value={asinSpec.productAssociation === "FEATURED"}
                              onColor="#FFF"
                              onChange={() => {
                                const updatedStatus =
                                  asinSpec?.productAssociation === "FEATURED" ? "NOT_FEATURED" : "FEATURED";
                                const updatedAsins = formState.conversionTrackingAsins?.map((asin) => {
                                  if (asin.productId === v.asin) {
                                    return { ...asin, productAssociation: updatedStatus };
                                  }
                                  return asin;
                                });
                                setValue("conversionTrackingAsins", updatedAsins, {
                                  shouldDirty: true,
                                });
                              }}
                            />
                          );
                        },
                      },
                    },
                  ]}
                  bulkActionOptions={[]}
                  hideCompareValue={true}
                  ExpandedContent={null}
                  isSaving={false}
                  isExpandable={false}
                />
              </Col>
            : null}
          </Row>

          {/* Targeting */}
          <Row className="py-3 dsp-form-section" ref={targetingSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">Targeting</Form.Label>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <Form.Group>
                  <Label>Frequencies</Label>
                  <CheckboxField
                    label={"Limit the number of times a shopper can view this ad"}
                    fieldName={"frequencies"}
                    deriveFieldValue={() => {
                      return formState.frequencies?.[0].frequencyType === "CUSTOM";
                    }}
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        setValue("frequencies.0", {
                          frequencyType: "CUSTOM",
                          maxImpressions: 4,
                          timeUnitCount: 1,
                          timeUnit: FREQUENCY_TIME_UNIT_OPTIONS[0].value,
                        });
                      } else {
                        setValue("frequencies.0.frequencyType", "UNCAPPED");
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            {formState.frequencies?.[0]?.frequencyType !== "UNCAPPED" && (
              <Row className="p-3">
                <Col xs={12} lg={8} xl={createMode ? 8 : 5} className="d-flex align-items-center gap-3 fs-5">
                  No more than{" "}
                  <InlineInputField
                    fieldName={`frequencies.0.maxImpressions`}
                    control={control}
                    rules={{
                      required: formState.frequencyCapped,
                      min: {
                        value: 1,
                        message: `Max impressions must be at least 1`,
                      },
                      max: {
                        value: 99000,
                        message: `Max impressions cannot be greater than 99000 `,
                      },
                    }}
                    width="125px"
                    errors={errors}
                    placeholder={null}
                    type={"number"}
                  />{" "}
                  times in{" "}
                  <InlineInputField
                    fieldName={`frequencies.0.timeUnitCount`}
                    control={control}
                    rules={{
                      required: formState.frequencyCapped,
                      min: {
                        value: 1,
                        message: `Count must be at least 1`,
                      },
                      max: {
                        value: 60,
                        message: `Count cannot be greater than 60 `,
                      },
                    }}
                    width="125px"
                    errors={errors}
                    placeholder={null}
                    type={"number"}
                  />{" "}
                  <InlineSelect
                    options={FREQUENCY_TIME_UNIT_OPTIONS}
                    fieldName={`frequencies.0.timeUnit`}
                    control={control}
                    rules={{ required: true }}
                  />
                </Col>
              </Row>
            )}
          </Row>
          <Row className="pt-4">
            <Col
              xs={12}
              lg={createMode ? 12 : 8}
              xl={12}
              className={`d-flex align-items-center justify-content-center gap-5 fs-5 ${createMode ? "pb-5" : ""}`}
            >
              {createMode ?
                <Button
                  variant="secondary"
                  onPress={() => {
                    onCancel();
                    reset(formatOrderFormData(initialFormData));
                  }}
                >
                  Cancel
                </Button>
              : <ConfirmationModal
                  title={"Revert Configuration Changes"}
                  confirmButtonText={`Confirm`}
                  confirmationMessage={
                    <p className="fs-4 w-75 mx-auto">
                      Are you sure you want to revert all changes made to this line item?
                    </p>
                  }
                  onConfirm={() => {
                    onCancel();
                    reset(formatOrderFormData(initialFormData));
                  }}
                  inlineModal={true}
                  modalButtonText={<button className={buttonRecipe({ variant: "secondary" })}>Cancel</button>}
                  inlineButtonContainerStyles={{}}
                />
              }

              <Button
                onClick={() => {
                  if (createMode) {
                    createOrder.mutate();
                  } else {
                    updateOrder.mutate();
                  }
                }}
                isDisabled={Object.keys(errors).length || !validateMandatoryOrderFormFields(formState)}
                isPending={updateOrder.isPending || createOrder.isPending}
              >
                {createMode ? "Create" : "Update"}
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

export default DSPOrderForm;
